import { Formik, Form, Field } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import DatePickerField from '../../Common/components/DatePickerField';
import LocationField from '../../Common/components/LocationField';
import useCreateActivation from './hooks/useCreateActivation';
import { useEffect, useState } from 'react';
import useJobHelpers from '@/src/Jobs/components/hooks/useJobHelpers';
import { Button } from '@/src/shadComponents/ui/button';
import { Label } from '@/src/shadComponents/ui/label';
import PageShell from '@/src/Common/components/PageShell';

interface ActivationFormValues {
    name: string;
    date: Date;
    location: string;
    v3Folder: string;
    jobId?: number;
}

export default function CreateActivation() {
    const initialValues: ActivationFormValues = {
        name: '',
        date: new Date(),
        location: '',
        v3Folder: '',
    };

    const navigate = useNavigate();
    const { jobId } = useParams();
    const [job, setJob] = useState<any>(null);
    const { getJob } = useJobHelpers();
    const { createActivation } = useCreateActivation();

    useEffect(() => {
        const getData = async () => {
            try {
                if (!jobId) return;
                // @ts-ignore
                const job = await getJob(+jobId);
                setJob(job);
            } catch (e) {
                console.log(e);
            }
        };
        getData();
        return () => {};

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    initialValues.jobId = jobId ? +jobId : undefined;

    return (
        <PageShell
            title="Create Activation"
            subtitle={job ? `For Job: ${job?.name}` : undefined}
            breadcrumbs={[
                { label: 'Activations', href: '/activations' },
                { label: 'Create' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, actions) => {
                    const activation = await createActivation(values);
                    actions.setSubmitting(false);

                    if (activation.id) {
                        navigate(`/activations/${activation.id}`);
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Basic Details
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Basic details about the activation.
                                    </p>
                                </div>
                                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 p-4 sm:p-0">
                                    <div className="sm:col-span-4">
                                        <Label htmlFor="name">
                                            Name
                                        </Label>
                                        <div className="mt-2">
                                            <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-border focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                <Field
                                                    id="name"
                                                    name="name"
                                                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-foreground placeholder:text-muted-foreground focus:ring-0 sm:text-sm sm:leading-6"
                                                    placeholder="Activation Name"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-span-full">
                                        <Label htmlFor="date">
                                            Date
                                        </Label>
                                        <DatePickerField
                                            id="date"
                                            name="date"
                                        />
                                    </div>
                                    <div className="col-span-full">
                                        <Label htmlFor="location">
                                            Location
                                        </Label>
                                        <LocationField
                                            className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-foreground placeholder:text-muted-foreground focus:ring-0 sm:text-sm sm:leading-6"
                                            name="location"
                                        />
                                    </div>
                                    <div className="col-span-full">
                                        <Label htmlFor="v3Folder">
                                            V3 Folder
                                        </Label>
                                        <div className="mt-2">
                                            <div className="mt-2">
                                                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-border focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                    <Field
                                                        id="v3Folder"
                                                        name="v3Folder"
                                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-foreground placeholder:text-muted-foreground focus:ring-0 sm:text-sm sm:leading-6"
                                                        placeholder="v3 folder name"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                disabled={props.isSubmitting}
                            >
                                Create
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
